/* Everything the character builder can pick from, read from the site's own content
   and passed through pickVersion so only the chosen expansion version shows. */

import { ClassFeature, classes, DhClass } from './content/classes';
import { ancestries } from './content/ancestries';
import { communities } from './content/communities';
import { transformations } from './content/transformations';
import allDomainCards from './content/domains/cards';
import equipment from './data/equipment.json';
import beastforms from './data/beastforms.json';
import items from './data/items.json';
import consumables from './data/consumables.json';
import { pickVersion, ContentVersion } from './content/sources';

export interface Weapon {
  name: string;
  damageType: string;
  trait: string;
  range: string;
  damage: string;
  burden: string;
  feature?: string | null;
  source?: string;
  tier?: number;
  /** "Primary" or "Secondary"; combat wheelchairs are primary weapons. */
  category?: string;
}

export interface Armor {
  name: string;
  major: number;
  severe: number;
  score: number;
  feature?: string | null;
  source?: string;
  tier?: number;
}

/** An entry of the Items or Consumables loot tables. */
export interface Loot {
  name: string;
  rarity: string;
  kind: 'Item' | 'Consumable';
  /** The item's subtype or the consumable's category. */
  type: string;
  text: string;
}

const LOOT: Loot[] = [
  ...items.map(({ name, rarity, subtype, text }) => ({ name, rarity, kind: 'Item' as const, type: subtype, text })),
  ...consumables.map(({ name, rarity, category, text }) => ({ name, rarity, kind: 'Consumable' as const, type: category, text })),
];

/** The Brawler's "I Am the Weapon" primary weapon; its trait is the player's choice. */
export const BRAWLERS_STRIKE: Weapon = {
  name: "Brawler's Strike",
  damageType: 'Physical',
  trait: '',
  range: 'Melee',
  damage: 'd8+d6',
  burden: '',
  feature: 'While this weapon is active, you gain a +1 bonus to your Evasion.',
};

const byName = <T extends { name: string }>(list: T[]) => {
  const map = new Map(list.map((item) => [item.name, item]));
  return (name: string) => map.get(name);
};

export const classDomains = (cls?: DhClass) => (cls?.stats?.domains ?? '').split(' & ').filter(Boolean);

export const classTagline = (cls: DhClass) => cls.tagline ?? cls.description?.[0] ?? '';

/** Features stored as plain strings or as { name, description }, always as objects. */
export const featureList = (list?: Array<ClassFeature | string>): ClassFeature[] =>
  (list ?? []).map((f) => (typeof f === 'string' ? { description: f } : f));

export const classFeatures = (cls?: DhClass) =>
  featureList(cls?.classFeatures ?? (cls?.classFeature ? [cls.classFeature] : []));

/** The Warlock's Patron's Pact: the player names their patron and, with the GM, its sphere of influence. */
export const hasPatron = (cls?: DhClass) => classFeatures(cls).some((f) => f.name === "Patron's Pact");

/** The elements Elemental Origin's Elementalist chooses from at character creation (SRD). */
export const ELEMENTS = ['Air', 'Earth', 'Fire', 'Lightning', 'Water'];

/** True when the subclass's foundation is Elementalist, so the character chooses an element. */
export const hasElement = (cls?: DhClass, subclass = '') => featureList(cls?.subclasses?.[subclass]?.foundation).some((f) => f.name === 'Elementalist');

export function buildCatalog(version: ContentVersion) {
  // Every tier: starting gear is Tier 1, but found or won gear can be any tier.
  const weapons = pickVersion(equipment.weapons as Weapon[], version);
  const primaries = weapons.filter((w) => w.category === 'Primary');
  const secondaries = weapons.filter((w) => w.category === 'Secondary');
  const wheelchairs = pickVersion(equipment.wheelchairs as Weapon[], version);
  const armors = pickVersion(equipment.armor as Armor[], version);
  const classList = pickVersion(classes, version);
  const ancestryList = pickVersion(ancestries, version);
  const communityList = pickVersion(communities, version);
  const transformationList = pickVersion(transformations, version);
  const cards = pickVersion(allDomainCards, version, (c) => c.domain);

  return {
    classes: classList,
    ancestries: ancestryList,
    communities: communityList,
    transformations: transformationList,
    primaries,
    secondaries,
    wheelchairs,
    armors,
    loot: LOOT,
    beastforms,
    /** Level 1 cards of the given domains. */
    startingCards: (domains: string[]) => cards.filter((c) => c.level === 1 && domains.includes(c.domain)),
    /** Every card of the given domains, lowest level first. */
    cardsIn: (domains: string[]) => cards.filter((c) => domains.includes(c.domain)).sort((a, b) => a.level - b.level),
    cls: byName(classList),
    ancestry: byName(ancestryList),
    community: byName(communityList),
    transformation: byName(transformationList),
    weapon: byName<Weapon>([...primaries, ...secondaries, ...wheelchairs, BRAWLERS_STRIKE]),
    armor: byName(armors),
    lootItem: byName(LOOT),
    card: byName(cards),
  };
}

export type Catalog = ReturnType<typeof buildCatalog>;
